<script setup lang="ts">
import { useBrowserStore } from '@apps/main/new-desktop/core/stores';

import { AppButton } from '@libs/ui';

import type { SuggestedActivityCardData } from '../../../models';

interface SuggestedActivityCardProps {
  activityCardData: SuggestedActivityCardData;
}
const props = defineProps<SuggestedActivityCardProps>();

interface SuggestedActivityCardEmits {
  (eventName: 'open-activity', activityCode: string): void;
}
const emit = defineEmits<SuggestedActivityCardEmits>();

const browserStore = useBrowserStore();
</script>

<template>
  <div
    class="suggested-activity-card"
    @click="emit('open-activity', props.activityCardData.productCode)"
  >
    <img
      class="card-image"
      :src="props.activityCardData.imageUrl"
      :alt="$t('a11y.activity_image')"
    />
    <div class="activity-card-content">
      <span class="card-name" :title="props.activityCardData.name">
        {{ props.activityCardData.name }}
      </span>
      <div class="card-footer">
        <div class="price">
          <span class="label">{{ $t('planner.from') }}</span>
          <span class="value">{{ props.activityCardData.price }}</span>
        </div>
        <AppButton
          v-if="browserStore.isSmallerThan600px"
          type="secondary"
          state="enabled"
          size="small"
          @click="emit('open-activity', props.activityCardData.productCode)"
        >
          {{ $t('planner.read_more') }}
        </AppButton>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.suggested-activity-card {
  padding: var(--spacing-12);
  margin: var(--spacing-12) var(--spacing-4);
  border-radius: var(--border-radius-8);
  box-shadow: var(--shadow);
  width: 430px;
  height: 134px;
  background-color: var(--color-white);
  display: flex;
  flex-shrink: 0;
  gap: var(--spacing-12);
  cursor: pointer;

  @include for-less-or-equal-than-599-px {
    width: 335px;
  }

  .card-image {
    border-radius: var(--border-radius-8);
    height: 110px;
    width: 100px;
    object-fit: cover;
  }

  .activity-card-content {
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: var(--spacing-8);

    .card-name {
      @include font-label-l-bold;
      color: var(--color-neutral-70);
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .card-footer {
      display: flex;
      justify-content: space-between;
      align-items: center;

      .price {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-4);
        align-self: flex-start;

        .label {
          @include font-label-s-light;
          color: var(--color-neutral-70);
        }

        .value {
          color: var(--color-neutral-70);
          @include font-label-l-bold;
        }
      }
    }
  }
}
</style>
